/**
 * UranIA PA — API client Cowork (redazione assistita di documenti).
 *
 * Wrapper su:
 *   - ``POST   /api/v1/cowork/sessions``
 *   - ``GET    /api/v1/cowork/sessions``
 *   - ``GET    /api/v1/cowork/sessions/{id}``
 *   - ``POST   /api/v1/cowork/sessions/{id}/attachments``  (multipart)
 *   - ``POST   /api/v1/cowork/sessions/{id}/messages``
 *   - ``POST   /api/v1/cowork/sessions/{id}/generate``
 *   - ``GET    /api/v1/cowork/sessions/{id}/document.docx`` (binary)
 */

import { useAuthStore } from '../auth/auth-store.ts';
import { loadSpaEnv } from '../env.ts';

export type CoworkStatus = 'collecting' | 'drafting' | 'ready' | 'failed';
export type CoworkRole = 'user' | 'assistant';

export interface CoworkMessageDto {
  readonly role: CoworkRole;
  readonly content: string;
  readonly created_at: string;
}

export interface CoworkAttachmentDto {
  readonly filename: string;
  readonly mime_type: string;
  readonly size_bytes: number;
  readonly motivo: string;
}

export interface CoworkSessionDto {
  readonly session_id: string;
  readonly status: CoworkStatus;
  readonly template_tipo_id: string;
  readonly messages: readonly CoworkMessageDto[];
  readonly attachments: readonly CoworkAttachmentDto[];
  readonly document_available: boolean;
  readonly error_detail: string | null;
  readonly created_at: string;
  readonly updated_at: string | null;
}

export interface ListCoworkSessionsDto {
  readonly items: readonly CoworkSessionDto[];
  readonly total: number;
}

export class CoworkApiError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  constructor(status: number, title: string, detail: string | null) {
    super(title);
    this.name = 'CoworkApiError';
    this.status = status;
    this.detail = detail;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

async function parseProblem(response: Response): Promise<CoworkApiError> {
  let title = response.statusText || 'http_error';
  let detail: string | null = null;
  try {
    const body = (await response.json()) as { title?: unknown; detail?: unknown };
    if (typeof body.title === 'string' && body.title.length > 0) title = body.title;
    if (typeof body.detail === 'string') detail = body.detail;
  } catch {
    // no problem+json
  }
  return new CoworkApiError(response.status, title, detail);
}

async function jsonRequest<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
  const response = await fetch(buildUrl(path), init);
  if (!response.ok) throw await parseProblem(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function createCoworkSession(
  templateTipoId: string,
): Promise<CoworkSessionDto> {
  return jsonRequest<CoworkSessionDto>('POST', '/api/v1/cowork/sessions', {
    template_tipo_id: templateTipoId,
  });
}

export async function listCoworkSessions(): Promise<ListCoworkSessionsDto> {
  return jsonRequest<ListCoworkSessionsDto>('GET', '/api/v1/cowork/sessions');
}

export async function getCoworkSession(
  sessionId: string,
): Promise<CoworkSessionDto> {
  return jsonRequest<CoworkSessionDto>(
    'GET',
    `/api/v1/cowork/sessions/${encodeURIComponent(sessionId)}`,
  );
}

export async function postCoworkMessage(
  sessionId: string,
  content: string,
): Promise<CoworkSessionDto> {
  return jsonRequest<CoworkSessionDto>(
    'POST',
    `/api/v1/cowork/sessions/${encodeURIComponent(sessionId)}/messages`,
    { content },
  );
}

export async function uploadCoworkAttachment(
  sessionId: string,
  file: File,
  motivo: string,
): Promise<CoworkSessionDto> {
  const form = new FormData();
  form.append('file', file);
  if (motivo.length > 0) form.append('motivo', motivo);
  const response = await fetch(
    buildUrl(
      `/api/v1/cowork/sessions/${encodeURIComponent(sessionId)}/attachments`,
    ),
    {
      method: 'POST',
      headers: { ...authHeader() },
      credentials: 'include',
      body: form,
    },
  );
  if (!response.ok) throw await parseProblem(response);
  return (await response.json()) as CoworkSessionDto;
}

export async function generateCoworkDocument(
  sessionId: string,
): Promise<CoworkSessionDto> {
  return jsonRequest<CoworkSessionDto>(
    'POST',
    `/api/v1/cowork/sessions/${encodeURIComponent(sessionId)}/generate`,
  );
}

export function coworkDocumentDownloadUrl(sessionId: string): string {
  return buildUrl(
    `/api/v1/cowork/sessions/${encodeURIComponent(sessionId)}/document.docx`,
  );
}

export async function downloadCoworkDocument(sessionId: string): Promise<Blob> {
  const response = await fetch(coworkDocumentDownloadUrl(sessionId), {
    method: 'GET',
    headers: { ...authHeader() },
    credentials: 'include',
  });
  if (!response.ok) throw await parseProblem(response);
  return await response.blob();
}
